import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGolfFlagHole,
  faPersonWalking,
  faUsers,
  faPersonCircleXmark,
  faTag,
} from "@fortawesome/pro-solid-svg-icons";
import React, { Fragment } from "react";
import "./CustomerTeeTimeCard.scss";
import { useTranslation } from "react-i18next";
import { displayCurrency } from "helpers/Helpers";
import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";
import { CustomIcon } from "components/customIcon/CustomIcon";
import Spin from "components/spin/spin";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Badge } from "components/badge/Badge";

interface IProps {
  time: string;
  isPrepaid: boolean;
  isShotgun: boolean;
  spotsRemaining: number;
  holes: number[];
  cartRule: string;
  nineHolePrice: number;
  eighteenHolePrice: number;
  dynamicPercentVariation: number;
  onClick: () => void;
  guestsAllowed: boolean;
  courseName?: string;
  divisionTitle?: string;
  singleBookings?: string;
  twosomeBookings?: string;
  threesomeBookings?: string;
  foursomeBookings?: string;
  quantityRemaining?: number;
  /** Full tee time capacity; used for allow_within_group (defaults to quantity remaining). */
  teeSize?: number;
  startingHole?: string;
  isLeague?: boolean;
  facilityName?: string;
  restricted?: boolean;
  isLottery?: boolean;
  displayDivision?: boolean;
  promotionType?: "percent" | "amount" | null;
  promotionValue?: number | null;
  promotionLabel?: string | null;
}

export function CustomerTeeTimeCard(props: IProps) {
  const {
    time,
    isPrepaid,
    isShotgun,
    spotsRemaining,
    holes,
    cartRule,
    nineHolePrice,
    eighteenHolePrice,
    dynamicPercentVariation,
    onClick,
    guestsAllowed,
    courseName,
    divisionTitle,
    singleBookings,
    twosomeBookings,
    threesomeBookings,
    foursomeBookings,
    quantityRemaining,
    teeSize,
    startingHole,
    isLeague,
    facilityName,
    isLottery,
    displayDivision,
    promotionType,
    promotionValue,
    promotionLabel,
  } = props;

  const windowSize = useWindowSize();
  const { t } = useTranslation();

  function convertCartRuleToDescription(cartRule: "none" | "optional" | "required") {
    if (cartRule === "none") {
      return t("Walking");
    } else if (cartRule === "optional") {
      return t("Carts Available");
    } else if (cartRule === "required") {
      return t("Carts Required");
    }
  }

  const hasPromotion = Boolean(promotionType) && promotionValue != null;

  const teeTimeBadges = (mobile = false) => {
    return (
      <>
        {isLeague && (
          <div className={classNames("customer-tee-time-prepaid", { "customer-tee-time-prepaid-mobile": mobile })}>
            {t("LEAGUE")}
          </div>
        )}
        {isPrepaid ? (
          <div className={classNames("customer-tee-time-prepaid", { "customer-tee-time-prepaid-mobile": mobile })}>
            {t("PRE-PAID")}
          </div>
        ) : null}
        {isShotgun ? (
          <>
            <div className={classNames("customer-tee-time-prepaid", { "customer-tee-time-prepaid-mobile": mobile })}>
              {"SHOTGUN"}
            </div>
            <div className={classNames("customer-tee-time-prepaid", { "customer-tee-time-prepaid-mobile": mobile })}>
              {startingHole}
            </div>
          </>
        ) : null}
        {isLottery ? (
          <div className={classNames("customer-tee-time-prepaid", { "customer-tee-time-prepaid-mobile": mobile })}>
            {t("LOTTERY")}
          </div>
        ) : null}
      </>
    );
  };

  const normalPrices = {
    nineHole: nineHolePrice + (nineHolePrice * (dynamicPercentVariation ?? 0)) / 100,
    eighteenHole: eighteenHolePrice + (eighteenHolePrice * (dynamicPercentVariation ?? 0)) / 100,
  };

  const applyPromotion = (price: number) => {
    if (!promotionType || promotionValue == null) {
      return price;
    }

    return promotionType === "percent"
      ? Math.max(price * (1 - promotionValue / 100), 0)
      : Math.max(price - promotionValue, 0);
  };

  const displayPrices = {
    nineHole: applyPromotion(normalPrices.nineHole),
    eighteenHole: applyPromotion(normalPrices.eighteenHole),
  };

  const getPlayersText = () => {
    const allowedSizes: number[] = [];
    const actualQty = quantityRemaining !== undefined ? quantityRemaining : spotsRemaining;
    const fullTeeSize = teeSize ?? actualQty;

    if (actualQty <= 0) {
      return `0 ${t("Players")}`;
    }

    for (let p = 1; p <= actualQty; p++) {
      let setting = "allow";
      if (p === 1) {
        setting = singleBookings ?? "allow";
      } else if (p === 2) {
        setting = twosomeBookings ?? "allow";
      } else if (p === 3) {
        setting = threesomeBookings ?? "allow";
      } else if (p === 4) {
        setting = foursomeBookings ?? "allow";
      }
      // Sizes above 4 have no named facility setting — allow when capacity exists

      if (setting === "none") {
        continue;
      }
      if (setting === "allow_within_group" && actualQty >= fullTeeSize) {
        continue;
      }

      allowedSizes.push(p);
    }

    const minAllowed = allowedSizes.length > 0 ? Math.min(...allowedSizes) : 1;
    const maxAllowed = allowedSizes.length > 0 ? Math.max(...allowedSizes) : spotsRemaining;

    if (minAllowed === maxAllowed) {
      return minAllowed === 1 ? `1 ${t("Player")}` : `${minAllowed} ${t("Players")}`;
    }
    return `${minAllowed} - ${maxAllowed} ${t("Players")}`;
  };

  return (
    <div className={classNames("customer-tee-time", { "customer-tee-time-promotion": hasPromotion })} onClick={onClick}>
      <div className="customer-tee-time-content">
        <div className="customer-tee-time-details-container">
          <div className="customer-tee-time-primary-details">
            <div
              className={classNames("customer-tee-time-active-time", {
                "customer-tee-time-active-time--promotion": hasPromotion,
              })}
            >
              {hasPromotion ? (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tee-time-promotion">{promotionLabel || t("Promotion")}</Tooltip>}
                >
                  <FontAwesomeIcon className="customer-tee-time-promotion-icon" icon={faTag} />
                </OverlayTrigger>
              ) : null}
              {time}
            </div>
            {teeTimeBadges()}
            {facilityName && windowSize?.width > MOBILE_WIDTH && (
              <p className="customer-tee-time-facility-name ">{facilityName}</p>
            )}
            {props?.restricted ? (
              <OverlayTrigger
                placement="auto"
                overlay={
                  <Tooltip id="Restricted-booking">
                    <>
                      {t("Restricted tee time")} <br />{" "}
                      {t("Your member rate is not valid at this time. Regular green fee applies.")}
                    </>
                  </Tooltip>
                }
              >
                <span>
                  <Badge type="error" size="small">
                    {t("Restricted booking")}{" "}
                    <span className="ml-2">
                      <FontAwesomeIcon icon={faCircleExclamation} />
                    </span>
                  </Badge>
                </span>
              </OverlayTrigger>
            ) : null}
          </div>
          {facilityName && windowSize?.width <= MOBILE_WIDTH && (
            <p className="customer-tee-time-facility-name ">{facilityName}</p>
          )}
          <div className="customer-tee-time-secondary-details">
            {courseName ? <p>{courseName}</p> : null}
            {displayDivision && divisionTitle ? <p>{divisionTitle}</p> : null}
          </div>
          <div
            className={classNames("customer-tee-time-primary-details customer-tee-time-badges-container", {
              "customer-tee-time-hide-badges-container": !(isLeague || isPrepaid || isShotgun || isLottery),
            })}
          >
            {teeTimeBadges(true)}
          </div>
          <div className="customer-tee-time-tertiary-details">
            <div className="customer-tee-time-detail">
              {windowSize.width < TABLET_WIDTH ? (
                <Fragment>
                  <span>{getPlayersText()}</span>
                </Fragment>
              ) : (
                <Fragment>
                  <FontAwesomeIcon className="customer-tee-time-detail-icon" icon={faUsers} />
                  <span>{getPlayersText()}</span>
                </Fragment>
              )}
            </div>
            <div className="customer-tee-time-detail">
              {windowSize.width < TABLET_WIDTH ? (
                <Fragment>
                  <span>{`${holes.join(" / ")} ${t("Holes")}`}</span>
                </Fragment>
              ) : (
                <Fragment>
                  <FontAwesomeIcon className="customer-tee-time-detail-icon" icon={faGolfFlagHole} />
                  <span>{`${holes.join(" / ")} ${t("Holes")}`}</span>
                </Fragment>
              )}
            </div>

            {cartRule === "none" || cartRule === "optional" || cartRule === "required" ? (
              <div className="customer-tee-time-detail">
                {windowSize.width < TABLET_WIDTH ? (
                  <Fragment>
                    <span>{convertCartRuleToDescription(cartRule)}</span>
                  </Fragment>
                ) : (
                  <Fragment>
                    {cartRule === "none" && (
                      <FontAwesomeIcon className="customer-tee-time-detail-icon" icon={faPersonWalking} />
                    )}

                    <div className="mr-2">
                      {cartRule === "optional" || cartRule === "required" ? (
                        <CustomIcon colour="blue" src="../../../public/images/powercart.svg" size="medium" />
                      ) : null}
                    </div>

                    <span>{convertCartRuleToDescription(cartRule)}</span>
                  </Fragment>
                )}
              </div>
            ) : (
              <div />
            )}

            {guestsAllowed ? (
              <div className="customer-tee-time-detail">
                {windowSize.width < TABLET_WIDTH ? (
                  <Fragment>
                    <FontAwesomeIcon className="customer-tee-time-detail-icon" icon={faPersonCircleXmark} />
                    {t("No Guests")}
                  </Fragment>
                ) : (
                  <Fragment>
                    <FontAwesomeIcon className="customer-tee-time-detail-icon" icon={faPersonCircleXmark} />
                    {t("No Guests")}
                  </Fragment>
                )}
              </div>
            ) : null}
          </div>
        </div>

        <div className="customer-tee-time-pricing">
          <div className="customer-tee-time-price-container">
            {eighteenHolePrice === undefined && nineHolePrice === undefined ? (
              <div style={{ display: "flex", height: "100%", margin: "auto" }}>
                <div className="text-center m-auto">
                  <div style={{ height: "32px" }}>
                    <Spin />
                  </div>
                </div>
              </div>
            ) : null}
            {nineHolePrice != null && (
              <div className="holes-pricing-container">
                <p className="customer-tee-time-holes-text">{t("{{numHoles}} Holes", { numHoles: holes[0] })}</p>
                <p
                  className={classNames("customer-tee-time-price", {
                    "customer-tee-time-promotion-price": hasPromotion,
                  })}
                >
                  {hasPromotion ? (
                    <span className="customer-tee-time-price-struck">
                      {displayCurrency("cad", normalPrices.nineHole)}
                    </span>
                  ) : null}
                  {displayCurrency("cad", displayPrices.nineHole)}
                </p>
              </div>
            )}
            {eighteenHolePrice != null && (
              <div className="holes-pricing-container">
                <p className="customer-tee-time-holes-text">
                  {t("{{numHoles}} Holes", { numHoles: holes.length > 1 ? holes[1] : 18 })}
                </p>
                <span
                  className={classNames("customer-tee-time-price", {
                    "customer-tee-time-promotion-price": hasPromotion,
                  })}
                >
                  {hasPromotion ? (
                    <span className="customer-tee-time-price-struck">
                      {displayCurrency("cad", normalPrices.eighteenHole)}
                    </span>
                  ) : null}
                  {displayCurrency("cad", displayPrices.eighteenHole)}
                </span>
              </div>
            )}
          </div>
          <button className="customer-tee-time-select-price">{isLottery ? t("Request Time") : t("Select")}</button>
        </div>
      </div>
    </div>
  );
}
